import React, {
  useState,
  useCallback,
  useMemo,
} from "react";
import {
  useConflictActions,
  useConflictData,
  useFilterData,
  useScheduleData,
  useViewData,
} from "../../store/magnetboardStore";
import MagnetboardUnplacedDropZone from "./MagnetboardUnplacedDropZone";
import MagnetboardQuickView from "./MagnetboardQuickView";
import MagnetboardDropPeriod from "./MagnetboardDropPeriod";
import { mapCoursePeriodSectionsSortedByQuarter } from "../../../../js/Mappers";
import { getCoursePeriodIdForPeriod } from "../../../../js/PeriodHelpers";
import {
  getCompactSectionAlignmentTemplate,
  getSectionTermFilterInfo,
} from "../../utils/sectionTermUtils";

const UNPLACED_FILTER_VALUE = 'unplaced';

const getSelectedFilterValues = (filterOptions = {}, filterKey) => {
  const options = Array.isArray(filterOptions?.[filterKey]) ? filterOptions[filterKey] : [];
  return options.filter(option => option.selected).map(option => `${option.value}`);
};

const sectionMatchesCourseViewPeriodTermFilters = (section, coursePeriodID, filterOptions = {}) => {
  const selected_periods = getSelectedFilterValues(filterOptions, 'periods');
  const selected_terms = getSelectedFilterValues(filterOptions, 'terms');
  const section_periods = Array.isArray(section?.course_periods) ? section.course_periods.map(period_id => `${period_id}`) : [];

  if(selected_periods.length > 0)
  {
    const matches_period = selected_periods.some(selected_period => (
      selected_period === UNPLACED_FILTER_VALUE
        ? section_periods.length === 0
        : section_periods.includes(selected_period)
    ));
    if(!matches_period) return false;
  }

  if(coursePeriodID && !section_periods.includes(`${coursePeriodID}`)) return false;

  if(selected_terms.length > 0)
  {
    const section_term_filter_value = `${getSectionTermFilterInfo(section).value}`;
    if(!selected_terms.some(selected_term => selected_term === section_term_filter_value)) return false;
  }

  return true;
};

const getVisibleSectionPlacements = (sections = [], visibleCoursePeriodIDs = []) => {
  const visible_period_id_set = new Set(visibleCoursePeriodIDs.map(course_period_id => `${course_period_id}`));

  return (Array.isArray(sections) ? sections : []).reduce((placements, section) => {
    const course_period_ids = (Array.isArray(section?.course_periods) ? section.course_periods : [])
      .filter(course_period_id => visible_period_id_set.has(`${course_period_id}`));

    if(course_period_ids.length > 0) {
      placements.push({ section, course_period_ids });
    }

    return placements;
  }, []);
};

const getStudentSectionPlacements = (studentSectionsByStudentCoursePeriod, studentID, visibleCoursePeriodIDs = []) => {
  const placements_by_section_id = new Map();

  visibleCoursePeriodIDs.forEach(course_period_id => {
    const period_sections = studentSectionsByStudentCoursePeriod?.[`${studentID}::${course_period_id}`] || [];

    period_sections.forEach(section => {
      const section_key = `${section?.section_id || ""}`;
      if(!section_key) return;

      const placement_info = placements_by_section_id.get(section_key) || {
        section,
        course_period_ids: [],
      };
      placement_info.course_period_ids.push(course_period_id);
      placements_by_section_id.set(section_key, placement_info);
    });
  });

  return Array.from(placements_by_section_id.values());
};

const MagnetboardPeriod = React.memo(
  ({
    entity,
    data_index,
    magnetboardOptions,
    scheduleData: {
      scheduleStructureData,
      setSections,
      sections,
      subsections,
      departments,
      studentMatchPercentages,
      schoolInfo,
      scheduleInfo,
      teachers,
      setTeachers,
      courses,
      classrooms,
      students,
      setStudents,
      labels,
      courseFillPercentagesByCourseId,
      studentSectionsByStudentCoursePeriod,
      studentConflictSectionLookup,
      sectionConflictSummaryBySectionId,
      sectionIsAllowedForCurrentUser = () => true,
    },
    magnetboardActions: { updateSectionPlacement, toggleLockSection, updateManualPlacement, toggleEditSection, duplicateSectionFromMagnetboard, deleteSectionFromMagnetboard, setDuplicateLoadingTarget, duplicateLoadingTargets, setDeleteLoadingTarget, deleteLoadingTargets, toggleEditStudentSchedule, toggleEditData, togglePeriodAvailability },
  }) => {
    const [selectedTeacherID, setSelectedTeacherID] = useState(null);
    const [selectedQuickViewDataID, setSelectedQuickViewDataID] =
      useState(null);
    const [selectedQuickViewSectionID, setSelectedQuickViewSectionID] =
      useState(null);
    const [activeClassroomDragRestrictionsByPeriod, setActiveClassroomDragRestrictionsByPeriod] = useState({});

    const {
      currentView,
      periodsToShow,
      currentDay,
      periodType,
      currentViewType,
    } = useViewData();
    const { scheduleType, currentScheduleVersionID } = useScheduleData();
    const {
      filterIDs,
      filtered,
      filteredData,
      labs_only_filtered,
      non_labs_only_filtered,
      no_classroom_filtered,
      has_classroom_filtered,
      singletons_filtered,
      doubletons_filtered,
      filterOptions,
    } = useFilterData();
    const {
      setConflictsLoading,
      setMatchPercent,
      setMatchPercentLoading,
    } = useConflictActions();
    const { conflicts } = useConflictData();

    const num_cols = useMemo(() => periodsToShow.length, [periodsToShow]);
    const row_style = { gridTemplateColumns: `170px repeat(${num_cols}, 1fr)` };
    const allowed_teacher_ids = Array.isArray(magnetboardOptions.allowed_teacher_ids) ? magnetboardOptions.allowed_teacher_ids : [];
    const allowed_classroom_ids = Array.isArray(magnetboardOptions.allowed_classroom_ids) ? magnetboardOptions.allowed_classroom_ids : [];
    // Functions
    const handleSelectTeacher = useCallback((selected_teacher) => {
      setSelectedQuickViewDataID(null);
      setSelectedTeacherID(selected_teacher);
    }, []);

    const handleSelectQuickViewDataID = useCallback(
      (selected_data_id, selected_section_id, e) => {
        setSelectedTeacherID(null);
        setSelectedQuickViewDataID(selected_data_id);
        setSelectedQuickViewSectionID(selected_section_id);
      },
      []
    );

    const handleClassroomDropHoverChange = useCallback((course_period_id, drag_restriction_info) => {
      setActiveClassroomDragRestrictionsByPeriod((current_restrictions) => {
        const restriction_key = `${course_period_id || ""}`;
        const next_restrictions = { ...current_restrictions };

        if (drag_restriction_info) {
          if (current_restrictions[restriction_key] === drag_restriction_info) return current_restrictions;
          next_restrictions[restriction_key] = drag_restriction_info;
        } else {
          if (!Object.prototype.hasOwnProperty.call(current_restrictions, restriction_key)) return current_restrictions;
          delete next_restrictions[restriction_key];
        }

        return next_restrictions;
      });
    }, []);

    const activeClassroomDragRestrictionInfo = useMemo(() => {
      if (currentView !== "classroom") return null;
      const active_period_key = Object.keys(activeClassroomDragRestrictionsByPeriod)[0];
      return active_period_key
        ? activeClassroomDragRestrictionsByPeriod[active_period_key]
        : null;
    }, [activeClassroomDragRestrictionsByPeriod, currentView]);

    // Common
    const entity_id = entity.data_id;
    const entity_info = entity.display_info;
    const lunch_schedule_period_id = entity_info.lunch_schedule_period_id;

    const department_list = useMemo(() => {
      if (currentView === "student") return false;

      const entity_departments = entity_info.departments;
      if (!entity_departments || entity_departments.length === 0 || departments.length === 0) return;
      
      const department_names = [];

      for (const department_id of entity_departments) {
        const department_index = departments.findIndex(
          (department) => department.department_id === department_id
        );
        const department =
          department_index !== -1
            ? departments.find(
                (department) => department.department_id === department_id
              ).department
            : null;
        if (department) department_names.push(department);
      }

      return department_names.join(", ");
    }, [entity_info, departments, currentView]);

    //// Teacher Data
    const teacher_not_allowed = useMemo(() => {
      if (currentView !== "teacher") return false;

      return (
        allowed_teacher_ids.length > 0 &&
        !allowed_teacher_ids.includes(entity_id)
      );
    }, [entity_id, allowed_teacher_ids, currentView]);

    const teacher_filtered_out = useMemo(() => {
      if (currentView !== "teacher") return false;

      return (
        (filterIDs.length > 0 && !filterIDs.includes(entity_id)) ||
        (filtered && !filteredData.includes(entity_id))
      );
    }, [entity_id, filterIDs, filtered, filteredData, currentView]);

    const teacher_selected = useMemo(() => {
      if (currentView !== "teacher" && currentView !== "course") return false;

      return selectedTeacherID === entity_id ? true : false;
    }, [currentView, selectedTeacherID, entity_id]);

    const quick_view_selected = useMemo(() => {
      if (currentView === "student") return false;
      return selectedQuickViewDataID === entity_id;
    }, [currentView, selectedQuickViewDataID, entity_id]);

    const teacher_sections = useMemo(() => {
      if (currentView !== "teacher") return [];

      return sections.filter(
        (section) =>
          (section.teacher_id === entity_id ||
            ("secondary_teachers" in section &&
              section.secondary_teachers.includes(entity_id)))
      );
    }, [currentView, sections, entity_id]);

    const teacher_unassigned_sections = useMemo(() => {
      if (currentView !== "teacher" && currentView !== "course") return [];

      const relevant_sections =
        currentView === "course"
          ? sections.filter((section) => sectionIsAllowedForCurrentUser(section) && section.course_id === entity_id)
          : teacher_sections;

      return relevant_sections.filter(
        (section) =>
          "course_periods" in section && section.course_periods.length === 0
      );
    }, [currentView, teacher_sections, sections, entity_id, sectionIsAllowedForCurrentUser]);

    const teacher_unassigned_sections_no_duplicates = useMemo(() => {
      if (currentView !== "teacher" && currentView !== "course") return [];

      // Unassigned sections without duplicates
      let logged_span_ids = [];
      return teacher_unassigned_sections.reduce(
        (results, unassigned_section) => {
          // Skip any previously seen span sections
          const span_id = unassigned_section.span_id;
          const is_lab = unassigned_section.is_lab;
          if (span_id && logged_span_ids.includes(span_id)) return results;
          if (is_lab === "1") return results;

          logged_span_ids.push(span_id);

          // If teacher just so happens to teach both the main and subsection, only list the main section in unassigned list
          const subsection_index = subsections.findIndex(
            (subsection) =>
              subsection.subsection_id === unassigned_section.section_id
          );
          const is_subsection = subsection_index !== -1 ? true : false;

          if (is_subsection) {
            const subsection_section_id =
              subsections[subsection_index].section_id;
            const section_info = sections.find(
              (section) => section.section_id === subsection_section_id
            );
            const parent_entity_id =
              currentView === "course" ? section_info.course_id : section_info.teacher_id;

            if (entity_id === parent_entity_id)
              return results;
          }

          results.push(unassigned_section);
          return results;
        },
        []
      );
    }, [teacher_unassigned_sections, subsections, sections, currentView, entity_id]);

    const teacher_has_unassigned_sections = useMemo(() => {
      return teacher_unassigned_sections_no_duplicates.length > 0;
    }, [teacher_unassigned_sections_no_duplicates]);

    const course_sections = useMemo(() => {
      if (currentView !== "course") return [];

      return sections.filter((section) => sectionIsAllowedForCurrentUser(section) && section.course_id === entity_id);
    }, [currentView, sections, entity_id, sectionIsAllowedForCurrentUser]);

    const current_entity_sections = currentView === "course" ? course_sections : teacher_sections;
    const visible_course_period_ids = useMemo(() => (
      periodsToShow.reduce((period_ids, period_info) => {
        const course_period_id = getCoursePeriodIdForPeriod(scheduleStructureData, period_info.period_id, {
          period_type: periodType,
          schedule_type: scheduleType,
          current_day: currentDay,
        });
        if (course_period_id) period_ids.push(course_period_id);
        return period_ids;
      }, [])
    ), [
      periodsToShow,
      scheduleStructureData,
      periodType,
      scheduleType,
      currentDay,
    ]);
    const section_alignment_template = useMemo(() => {
      if (currentView === "student") {
        return getCompactSectionAlignmentTemplate(
          getStudentSectionPlacements(studentSectionsByStudentCoursePeriod, entity_id, visible_course_period_ids)
        );
      }

      if (currentView === "classroom") {
        const classroom_row_sections = sections.filter(section => (
          `${section.classroom_id}` === `${entity_id}` &&
          sectionIsAllowedForCurrentUser(section)
        ));
        return getCompactSectionAlignmentTemplate(
          getVisibleSectionPlacements(classroom_row_sections, visible_course_period_ids)
        );
      }

      if (currentView === "course") {
        const course_row_sections = course_sections.filter(section => {
          return sectionMatchesCourseViewPeriodTermFilters(section, null, filterOptions);
        });
        return getCompactSectionAlignmentTemplate(
          getVisibleSectionPlacements(course_row_sections, visible_course_period_ids)
        );
      }

      if (currentView === "teacher") {
        return getCompactSectionAlignmentTemplate(
          getVisibleSectionPlacements(current_entity_sections, visible_course_period_ids)
        );
      }

      return getCompactSectionAlignmentTemplate(
        getVisibleSectionPlacements(current_entity_sections, visible_course_period_ids)
      );
    }, [
      currentView,
      visible_course_period_ids,
      studentSectionsByStudentCoursePeriod,
      entity_id,
      sections,
      sectionIsAllowedForCurrentUser,
      course_sections,
      filterOptions,
      current_entity_sections,
    ]);

    //// Classroom Data
    const classroom_not_allowed = useMemo(() => {
      if (currentView !== "classroom") return false;

      return (
        allowed_classroom_ids.length > 0 &&
        !allowed_classroom_ids.includes(entity_id)
      );
    }, [entity_id, allowed_classroom_ids, currentView]);

    const classroom_filtered_out = useMemo(() => {
      if (currentView !== "classroom") return false;

      return (
        (filterIDs.length > 0 && !filterIDs.includes(entity_id)) ||
        (filtered && !filteredData.includes(entity_id))
      );
    }, [entity_id, filterIDs, filtered, filteredData, currentView]);

    //// Student Data
    const { student_first_name, student_last_name, student_grade } =
      useMemo(() => {
        if (currentView !== "student") return {};

        return {
          student_first_name: entity_info.first_name,
          student_last_name: entity_info.last_name,
          student_grade: entity_info.grade,
        };
      }, [entity_info, currentView]);

    const { best_student_match, student_indicator_color } = useMemo(() => {
      if (currentView !== "student") return {};
      if(!studentMatchPercentages) return {};

      // Best match percentage between match_percent and percent_schedule_full
      const student_match_percent =
        entity_id in studentMatchPercentages
          ? studentMatchPercentages[entity_id].match_percent
          : 0;
      const student_percent_schedule_full =
        entity_id in studentMatchPercentages
          ? studentMatchPercentages[entity_id].percent_schedule_full
          : 0;
      const best_student_match = Math.round(
        Math.max(student_match_percent, student_percent_schedule_full)
      );

      return {
        best_student_match,
        student_indicator_color:
          best_student_match === 100
            ? "green-stroke"
            : best_student_match >= 75 && best_student_match < 100
            ? "yellow-stroke"
            : "red-stroke",
      };
    }, [entity_id, studentMatchPercentages, currentView]);

    const {
      course_fill_percent_raw,
      course_fill_percent_ring,
      course_indicator_color,
    } = useMemo(() => {
      if (currentView !== "course") return {};

      const fill_info =
        courseFillPercentagesByCourseId && entity_id in courseFillPercentagesByCourseId
          ? courseFillPercentagesByCourseId[entity_id]
          : {};
      const raw_fill_percent = Number.isFinite(fill_info.raw_fill_percent)
        ? fill_info.raw_fill_percent
        : 0;
      const ring_fill_percent = Number.isFinite(fill_info.ring_fill_percent)
        ? fill_info.ring_fill_percent
        : Math.min(raw_fill_percent, 100);

      return {
        course_fill_percent_raw: raw_fill_percent,
        course_fill_percent_ring: ring_fill_percent,
        course_indicator_color:
          raw_fill_percent >= 100
            ? "green-stroke"
            : raw_fill_percent >= 90
            ? "green-stroke"
            : raw_fill_percent >= 75
            ? "yellow-stroke"
            : "red-stroke",
      };
    }, [currentView, entity_id, courseFillPercentagesByCourseId]);

    const show_circular_indicator =
      currentView === "student" || currentView === "course";
    const indicator_ring_percent =
      currentView === "course" ? course_fill_percent_ring : best_student_match;
    const indicator_text_percent =
      currentView === "course" ? course_fill_percent_raw : best_student_match;
    const indicator_color =
      currentView === "course" ? course_indicator_color : student_indicator_color;

    const mb_row_parent_class =
      currentView === "teacher" || currentView === "course"
        ? `${data_index % 5 === 0 ? "page-break" : ""} ${
            teacher_selected ? "mb-schedule-row-selected" : ""
          } ${teacher_not_allowed || teacher_filtered_out ? "hide" : ""}`
        : currentView === "classroom"
        ? `${classroom_not_allowed || classroom_filtered_out ? " hide" : ""}`
        : "";
    const mb_schedule_row_name_container_class =
      currentView === "teacher" || currentView === "course"
        ? `${teacher_has_unassigned_sections && " left-border-red-thick"}`
        : currentView === "classroom"
        ? `left-border-orange-thick`
        : "";

    return (
      <div
        key={data_index}
        className={`mb-schedule-row-parent ${mb_row_parent_class}`}
      >
        <div
          className={`mb-row mb-schedule-row ${
            currentView === "teacher" || currentView === "course" ? "cursor-pointer" : ""
          }`}
          style={row_style}
          data-ref={entity_id}
        >
          <div className="mb-schedule-main-inner-box">
            <div
              className={`mb-schedule-row-name-container ${mb_schedule_row_name_container_class}`}
              onClick={
                currentView === "teacher" || currentView === "course"
                  ? () => handleSelectTeacher(entity_id)
                  : null
              }
              style={
                currentView === "student" || currentView === "course"
                  ? { display: "grid", gridTemplateColumns: "auto 1fr" }
                  : {}
              }
            >
              {show_circular_indicator && (
                <svg
                  viewBox="0 0 36 36"
                  className={`mb-circular-chart ${indicator_color}`}
                >
                  <path
                    className="mb-circle-bg"
                    d="M18 2.0845
                    a 15.9155 15.9155 0 0 1 0 31.831
                    a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                  <path
                    className="mb-circle"
                    strokeDasharray={`${indicator_ring_percent}, 100`}
                    d="M18 2.0845
                    a 15.9155 15.9155 0 0 1 0 31.831
                    a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                  <text x="18" y="20.35" className="mb-percentage">
                    {indicator_text_percent}%
                  </text>
                </svg>
              )}
              <div>
                <div
                  className={`mb-schedule-row-name ${magnetboardOptions.allow_hotlinks !== false ? 'mb-schedule-row-name-link' : ''}`}
                  onClick={magnetboardOptions.allow_hotlinks !== false ? (event) => {
                    event.stopPropagation();
                    toggleEditData(currentView, entity_id);
                  } : null}
                >
                  {currentView === "student"
                    ? `${student_last_name}, ${student_first_name}`
                    : entity.display_name}
                </div>
                <div className="mb-schedule-row-department dark-gray-text">
                  {currentView === "student" ? student_grade : department_list}
                </div>

                {currentView === "student" && (
                  <div
                    className="blue-link small-text full-width"
                    onClick={() => toggleEditStudentSchedule(entity_id)}
                  >
                    Edit Schedule
                  </div>
                )}
                {(currentView === "teacher" || currentView === "course") && (
                  <div
                    className={`extra-small-text ${
                      teacher_has_unassigned_sections
                        ? "red-link"
                        : "green-link"
                    } underline-dashed`}
                    style={{ margin: "3px auto 0px auto" }}
                  >
                    {" "}
                    {teacher_unassigned_sections_no_duplicates.length} unplaced sections
                  </div>
                )}
              </div>
            </div>
          </div>

	          {periodsToShow.map((period_info, index) => {
	            const period_id = period_info.period_id;
	            const period_name = period_info.period_name;
	            const course_period_id = getCoursePeriodIdForPeriod(scheduleStructureData, period_id, {
	              period_type: periodType,
	              schedule_type: scheduleType,
	              current_day: currentDay,
	            });

            if (!course_period_id) return null;

            const is_lunch_period =
              currentViewType === "day" &&
              period_id === lunch_schedule_period_id
                ? true
                : false;

            // classroom
            const data_sections =
              currentView === "classroom"
                ? sections.filter(
                    (section) =>
                      section.classroom_id === entity_id &&
                      sectionIsAllowedForCurrentUser(section) &&
                      section.course_periods.includes(course_period_id)
                  )
                : currentView === "course"
                ? course_sections.filter((section) =>
                    sectionMatchesCourseViewPeriodTermFilters(section, course_period_id, filterOptions)
                  )
                : currentView === "student"
                ? studentSectionsByStudentCoursePeriod?.[`${entity_id}::${course_period_id}`] || []
                : [];

            const restricted_periods =
              currentView === "teacher" || currentView === "course"
                ? Array.isArray(entity_info.period_restrictions)
                  ? entity_info.period_restrictions
                  : []
                : [];
            const is_restricted_period = restricted_periods
              .map((restricted_period) => `${restricted_period}`)
              .includes(`${course_period_id}`);

            const course_period_sections_sorted_by_quarter =
              data_sections.length > 0
                ? mapCoursePeriodSectionsSortedByQuarter(data_sections)
                : [];

            return (
              <MagnetboardDropPeriod
                key={index}
                schoolInfo={schoolInfo}
                scheduleInfo={scheduleInfo}
                scheduleStructureData={scheduleStructureData}
                scheduleType={scheduleType}
                currentView={currentView}
                currentViewType={currentViewType}
                currentDay={currentDay}
                currentScheduleVersionID={currentScheduleVersionID}
                magnetboardOptions={magnetboardOptions}
                teachers={teachers}
                setTeachers={setTeachers}
                courses={courses}
                classrooms={classrooms}
                students={students}
                setStudents={setStudents}
                sections={sections}
                setSections={setSections}
                subsections={subsections}
                labels={labels}
                setConflictsLoading={setConflictsLoading}
                setMatchPercent={setMatchPercent}
                setMatchPercentLoading={setMatchPercentLoading}
                labsOnlyFiltered={labs_only_filtered}
                nonLabsOnlyFiltered={non_labs_only_filtered}
                singletonsFiltered={singletons_filtered}
                doubletonsFiltered={doubletons_filtered}
                noClassroomFiltered={no_classroom_filtered}
                hasClassroomFiltered={has_classroom_filtered}
                toggleEditSection={toggleEditSection}
                duplicateSectionFromMagnetboard={duplicateSectionFromMagnetboard}
                deleteSectionFromMagnetboard={deleteSectionFromMagnetboard}
                setDuplicateLoadingTarget={setDuplicateLoadingTarget}
                deleteLoadingTargets={deleteLoadingTargets}
                setDeleteLoadingTarget={setDeleteLoadingTarget}
                updateSectionPlacement={updateSectionPlacement}
                updateManualPlacement={updateManualPlacement}
                toggleLockSection={toggleLockSection}
                isLunchPeriod={is_lunch_period}
                teacherID={currentView === "teacher" ? entity_id : null}
                courseID={currentView === "course" ? entity_id : null}
                classroomID={currentView === "classroom" ? entity_id : null}
                studentID={currentView === "student" ? entity_id : null}
                periodID={period_id}
                periodName={period_name}
                coursePeriodID={course_period_id}
                isRestrictedPeriod={is_restricted_period}
                togglePeriodAvailability={togglePeriodAvailability}
                isDuplicateLoading={!!duplicateLoadingTargets[`${entity_id || ""}::${course_period_id || ""}`]}
                handleSelectQuickViewDataID={
                  currentView !== "student" ? handleSelectQuickViewDataID : null
                }
                selectedQuickViewSectionID={
                  currentView !== "student" ? selectedQuickViewSectionID : null
                }
                dataSections={
	                  currentView === "teacher"
	                    ? current_entity_sections :
	                  currentView === "course"
	                    ? data_sections :
	                  currentView === "student"
	                    ? data_sections
	                    : course_period_sections_sorted_by_quarter
	                }
                sectionAlignmentTemplate={section_alignment_template}
                studentConflictSectionLookup={studentConflictSectionLookup}
                sectionConflictSummaryBySectionId={sectionConflictSummaryBySectionId}
                sectionIsAllowedForCurrentUser={sectionIsAllowedForCurrentUser}
                activeDragRestrictionInfo={activeClassroomDragRestrictionInfo}
                onDropHoverChange={
                  currentView === "classroom" ? handleClassroomDropHoverChange : null
                }
	              />
	            );
          })}
        </div>

        {/* Just teacher */}
        {(currentView === "teacher" || currentView === "course") && teacher_selected && (
          <div className="mb-unassigned-sections-parent-container">
            <div></div>
            <MagnetboardUnplacedDropZone
              schoolInfo={schoolInfo}
              scheduleInfo={scheduleInfo}
              scheduleStructureData={scheduleStructureData}
              scheduleType={scheduleType}
              currentView={currentView}
              currentViewType={currentViewType}
              currentDay={currentDay}
              currentScheduleVersionID={currentScheduleVersionID}
              magnetboardOptions={magnetboardOptions}
              teachers={teachers}
              setTeachers={setTeachers}
              courses={courses}
              classrooms={classrooms}
              students={students}
              setStudents={setStudents}
              sections={sections}
              setSections={setSections}
              subsections={subsections}
              labels={labels}
              setConflictsLoading={setConflictsLoading}
              setMatchPercent={setMatchPercent}
              setMatchPercentLoading={setMatchPercentLoading}
              toggleEditSection={toggleEditSection}
              duplicateSectionFromMagnetboard={duplicateSectionFromMagnetboard}
              deleteSectionFromMagnetboard={deleteSectionFromMagnetboard}
              setDuplicateLoadingTarget={setDuplicateLoadingTarget}
              deleteLoadingTargets={deleteLoadingTargets}
              setDeleteLoadingTarget={setDeleteLoadingTarget}
              updateManualPlacement={updateManualPlacement}
              sectionIsAllowedForCurrentUser={sectionIsAllowedForCurrentUser}
              toggleLockSection={toggleLockSection}
              sectionConflictSummaryBySectionId={sectionConflictSummaryBySectionId}
              dataID={entity_id}
              unassignedSections={teacher_unassigned_sections_no_duplicates}
              handleSelectData={handleSelectTeacher}
            />
          </div>
        )}
        {currentView !== "student" && quick_view_selected && (
          <div className="mb-unassigned-sections-parent-container">
            <div></div>
            <MagnetboardQuickView
              sectionID={selectedQuickViewSectionID}
              schoolInfo={schoolInfo}
              currentScheduleVersionID={currentScheduleVersionID}
              scheduleStructureData={scheduleStructureData}
              scheduleType={scheduleType}
              scheduleInfo={scheduleInfo}
              currentView={currentView}
              currentViewType={currentViewType}
              currentDay={currentDay}
              sections={sections}
              setSections={setSections}
              subsections={subsections}
              labels={labels}
              teachers={teachers}
              courses={courses}
              classrooms={classrooms}
              students={students}
              conflicts={conflicts}
              handleSelectQuickViewDataID={handleSelectQuickViewDataID}
            />
          </div>
        )}
      </div>
    );
  }
);

export default MagnetboardPeriod;
